import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import productsData from "../data/mydata.json";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function SearchResults({ onOpenAuth, onOpenMenu }) {
  const query = useQuery();
  const navigate = useNavigate();
  const q = (query.get("q") || "").trim();

  const results = useMemo(() => {
    if (!q) return [];
    const lower = q.toLowerCase();
    return productsData.filter(
      (p) =>
        (p.title && p.title.toLowerCase().includes(lower)) ||
        (p.sku && p.sku.toLowerCase().includes(lower))
    ).slice(0, 24);
  }, [q]);

  const hasResults = q && results.length > 0;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex max-w-8xl gap-6 px-4 py-6 md:px-6">
        <Sidebar />

        <section className="flex-1 space-y-6">
          {!q && (
            <p className="mt-10 text-sm text-slate-500">
              Start by typing a product name in the search box above.
            </p>
          )}

          {q && hasResults && (
            <>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">
                  Search results:{" "}
                  <span className="font-bold">&quot;{q}&quot;</span>
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Found {results.length} items matching your selection.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {results.map((product, idx) => (
                  <ProductCard
                    key={`${product.pid || product.sku}-${idx}`}
                    accent={["orange", "blue", "green", "purple"][idx % 4]}
                    product={product}
                    onClick={() =>
                      navigate(
                        `/product/${encodeURIComponent(product.pid || product.sku)}`
                      )
                    }
                  />
                ))}
              </div>
            </>
          )}

          {q && !hasResults && (
            <div className="flex h-[60vh] flex-col items-center justify-center text-center">
              <div className="mb-4 text-6xl text-orange-500">⛔</div>
              <h2 className="mb-2 text-xl font-semibold text-slate-900">
                Search results: &quot;{q}&quot;
              </h2>
              <p className="text-sm text-orange-500">
                No products we found matching your selection.
              </p>
            </div>
          )}
        </section>

        <aside className="hidden w-64 rounded-2xl border border-slate-200 bg-white p-4 text-xs md:block">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">
            Your Basket
          </h2>
          <div className="space-y-2 text-[11px] text-slate-600">
            <p>No items added yet.</p>
            <p className="mt-2 flex items-center justify-between text-sm">
              <span className="font-semibold">Est. Total</span>
              <span>£0.00</span>
            </p>
            <button className="mt-3 w-full rounded-full bg-indigo-600 py-2 text-xs font-semibold text-white">
              Compare &amp; Checkout →
            </button>
          </div>
        </aside>
      </main>

      <Footer />
    </div>
  );
}

export default SearchResults;

